import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { Button } from "@dmca/frontend/components/button";
import { Input } from "@dmca/frontend/components/input";
import { ConfirmationModal } from "@dmca/frontend/components/modal";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import type { IMachinery } from "@dmca/models/interface/machinery";

export const Route = createFileRoute(
  "/_authed/_drawerLayout/admin/machinery/machines/$id"
)({
  component: RouteComponent,
  loader: async ({ context: { trpcQcProxy: trpc, qc }, params: { id } }) => {
    const [machinery] = await Promise.all([
      qc.ensureQueryData(trpc.machinery.getAll.queryOptions()),
      qc.ensureQueryData(trpc.plant.getAll.queryOptions()),
    ]);
    if (!machinery.find((m) => m.id === id)) throw notFound();
  },
});

function RouteComponent() {
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const { id } = Route.useParams();
  const navigate = Route.useNavigate();

  const { data: machine } = useQuery(
    trpc.machinery.getAll.queryOptions(undefined, {
      select: (data) => data.find((m) => m.id === id) as IMachinery,
    })
  );
  const { data: plants } = useQuery(trpc.plant.getAll.queryOptions());

  const [name, setName] = useState("");
  const [plantId, setPlantId] = useState("");
  const [type, setType] = useState<"forklift" | "crane" | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    if (!machine) return;
    setName(machine.name);
    setPlantId(machine.plantId);
    setType(machine.type);
  }, [machine]);

  const updateMutation = useMutation(
    trpc.machinery.update.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries(trpc.machinery.getAll.queryOptions());
        toast.success("Machine updated successfully.");
      },
      onError: () => {
        toast.error("An error occurred. Please try again or contact support.");
      },
    })
  );

  const deleteMutation = useMutation(
    trpc.machinery.delete.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries(trpc.machinery.getAll.queryOptions());
        toast.success("Machine deleted.");
        navigate({ to: "/admin/machinery" });
      },
      onError: () => {
        toast.error("An error occurred. Please try again or contact support.");
      },
    })
  );

  if (!machine) return null;

  const isDirty =
    name.trim() !== machine.name ||
    plantId !== machine.plantId ||
    type !== machine.type;

  return (
    <>
      <Stack spacing={2} sx={{ mt: 1 }} component="form">
        <Input
          label="Machine Name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!name.trim()}
          errorText="Machine name is required."
        />
        <FormControl fullWidth size="small" required>
          <InputLabel>Plant</InputLabel>
          <Select
            value={plantId}
            label="Plant"
            onChange={(e) => setPlantId(e.target.value)}
          >
            {plants?.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Stack spacing={1}>
          <Typography variant="body2" color="text.secondary">
            Type
          </Typography>
          <ToggleButtonGroup
            value={type}
            exclusive
            onChange={(_, val) => val && setType(val)}
            size="small"
            fullWidth
          >
            <ToggleButton value="forklift">Forklift</ToggleButton>
            <ToggleButton value="crane">Crane</ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        <Button
          fullWidth
          disabled={!isDirty || !name.trim() || !plantId || !type}
          loading={updateMutation.isPending}
          type="submit"
          onClick={(e) => {
            e.preventDefault();
            if (!type) return;
            updateMutation.mutate({
              id,
              name: name.trim(),
              plantId,
              type,
            });
          }}
        >
          Save Changes
        </Button>
      </Stack>
      <Divider sx={{ my: 3 }} />
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          Advanced
        </AccordionSummary>
        <AccordionDetails>
          <Button
            fullWidth
            color="error"
            onClick={() => setShowDeleteModal(true)}
            loading={deleteMutation.isPending}
          >
            Delete Machine
          </Button>
        </AccordionDetails>
      </Accordion>
      {showDeleteModal && (
        <ConfirmationModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={() => {
            deleteMutation.mutate({ id });
            setShowDeleteModal(false);
          }}
        >
          <Typography>
            Are you sure you want to delete <strong>{machine.name}</strong>?
            This will also remove all status history for this machine.
          </Typography>
          <Typography fontWeight="bold">THIS PROCESS IS IRREVERSIBLE!</Typography>
        </ConfirmationModal>
      )}
    </>
  );
}
